import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { baseAPICall } from 'services/baseAPICall'
import { clientInstance } from 'utils/http-client'

// Types
export interface CampaignSettings {
  id: number
  name: string
  status: string
  max_emails_per_day: number
  max_new_leads_per_day: number
  plain_text: boolean
  open_tracking: boolean
  can_unsubscribe: boolean
  unsubscribe_text: string
}

export interface UpdateCampaignSettingsPayload {
  name?: string
  max_emails_per_day?: number
  max_new_leads_per_day?: number
  plain_text?: boolean
  open_tracking?: boolean
  can_unsubscribe?: boolean
  unsubscribe_text?: string
}

// Query keys
export const campaignSettingsKeys = {
  all: ['campaign-settings'] as const,
  byCampaign: (campaignId: number) =>
    [...campaignSettingsKeys.all, 'campaign', campaignId] as const,
}

// API calls
const getCampaignSettings = async (
  campaignId: number
): Promise<CampaignSettings | null> => {
  return baseAPICall<CampaignSettings | null>(() =>
    clientInstance.get(`/emailbison/campaigns/${campaignId}/settings/`)
  )
}

const updateCampaignSettings = async (
  campaignId: number,
  payload: UpdateCampaignSettingsPayload
): Promise<CampaignSettings> => {
  return baseAPICall<CampaignSettings>(() =>
    clientInstance.patch(
      `/emailbison/campaigns/${campaignId}/settings/`,
      payload
    )
  )
}

// Hooks
export const useGetCampaignSettings = (campaignId: number, enabled = true) => {
  return useQuery({
    queryKey: campaignSettingsKeys.byCampaign(campaignId),
    queryFn: () => getCampaignSettings(campaignId),
    enabled: enabled && campaignId > 0,
  })
}

export const useUpdateCampaignSettings = (campaignId: number) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: UpdateCampaignSettingsPayload) =>
      updateCampaignSettings(campaignId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(
        campaignSettingsKeys.byCampaign(campaignId),
        data
      )
    },
  })
}
